import React from 'react';
import { Box, Container, Typography, Button, Stack, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link as ScrollLink } from 'react-scroll';

const badges = [
  'Inventarios',
  'Facturación',
  'Órdenes de servicio',
  'Control financiero',
  'Historial de clientes',
  'Reportes',
];

const HeroSection = () => {
  return (
    <Box
      id="hero"
      sx={{
        background: 'linear-gradient(135deg, #083a94 0%, #0d4dbf 60%, #0a3fa8 100%)',
        color: 'white',
        py: { xs: 10, md: 16 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Fondo decorativo */}
      <Box
        sx={{
          position: 'absolute',
          top: -120,
          right: -120,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'rgba(56,189,248,0.07)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -80,
          left: -80,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(56,189,248,0.05)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg">
        <Stack spacing={5} alignItems={{ xs: 'center', md: 'flex-start' }} sx={{ maxWidth: 780 }}>

          {/* Tag superior */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(56,189,248,0.15)',
                border: '1px solid rgba(56,189,248,0.4)',
                borderRadius: 10,
                px: 2,
                py: 0.7,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#38BDF8' }} />
              <Typography variant="caption" sx={{ color: '#38BDF8', fontWeight: 600, letterSpacing: 1 }}>
                SOLUCIONES A MEDIDA · SIN SOFTWARE GENÉRICO
              </Typography>
            </Box>
          </motion.div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.08,
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '2.4rem', sm: '3rem', md: '3.8rem', lg: '4.4rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Automatizamos tu negocio{' '}
              <Box component="span" sx={{ color: '#38BDF8' }}>
                exactamente como lo necesitas
              </Box>
            </Typography>
          </motion.div>

          {/* Subtítulo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.78)',
                fontWeight: 400,
                lineHeight: 1.6,
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                maxWidth: 620,
              }}
            >
              Analizamos cómo opera tu empresa y construimos la solución digital
              que realmente necesitas — sin plantillas, sin intermediarios.
            </Typography>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
              <ScrollLink to="contact" smooth duration={600} offset={-70}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: '#38BDF8',
                    color: '#083a94',
                    fontWeight: 700,
                    fontSize: '1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#7DD3FC' },
                    boxShadow: '0 8px 32px rgba(56,189,248,0.35)',
                  }}
                >
                  Hablemos de tu proyecto
                </Button>
              </ScrollLink>
              <ScrollLink to="portfolio" smooth duration={600} offset={-70}>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.4)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#38BDF8',
                      bgcolor: 'rgba(56,189,248,0.08)',
                    },
                  }}
                >
                  Ver lo que construimos
                </Button>
              </ScrollLink>
            </Stack>
          </motion.div>

          {/* Chips de módulos */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
            >
              {badges.map((b) => (
                <Chip
                  key={b}
                  label={b}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    fontWeight: 500,
                    '&:hover': { bgcolor: 'rgba(56,189,248,0.2)' },
                  }}
                />
              ))}
            </Stack>
          </motion.div>

        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection;
